import { useState, useCallback } from 'react';
import {
  Eye,
  MousePointerClick,
  Percent,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  FileText,
  Loader2,
  BarChart3,
  Megaphone,
} from 'lucide-react';
import { TierProgress } from '@/components/TierProgress';
import { TierUpgradeModal } from '@/components/TierUpgradeModal';
import { BalanceWidget } from '@/components/BalanceWidget';
import { PartnerNextStepsCard } from '@/components/PartnerNextStepsCard';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { motion } from 'framer-motion';
import { useDashboard } from '@/hooks/useDashboard';
import { api } from '@/lib/api';
import { KPICard, useToast } from '@mustauto/ui/web';
import { ROIHero } from '@/components/ROIHero';
import { ExportDropdown } from '@/components/ui/export-dropdown';
import { Card, CardHeader, CardTitle, CardContent } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { SkeletonKPIGrid, SkeletonChart, SkeletonTable } from '@mustauto/ui/web';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mustauto/ui/web';
import { EmptyState, ErrorState } from '@mustauto/ui/web';
import { formatNumber, formatPercent, formatReais } from '@/lib/utils';

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getPreviousMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const date = new Date(y, m - 2, 1); // month is 0-indexed, so m-1 is current, m-2 is previous
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function calcDelta(current: number, previous: number): { delta: string; deltaType: 'positive' | 'negative' | 'neutral' } {
  if (previous === 0) {
    if (current === 0) return { delta: '0%', deltaType: 'neutral' };
    return { delta: '+100%', deltaType: 'positive' };
  }
  const pct = ((current - previous) / previous * 100);
  const sign = pct >= 0 ? '+' : '';
  const deltaType = pct > 0 ? 'positive' : pct < 0 ? 'negative' : 'neutral';
  return { delta: `${sign}${pct.toFixed(1)}%`, deltaType };
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function DashboardPage() {
  const [month, setMonth] = useState(getCurrentMonth);
  const [compare, setCompare] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { data, isLoading, isError, error, refetch } = useDashboard(month);
  const prevMonth = getPreviousMonth(month);
  const { data: prevData } = useDashboard(compare ? prevMonth : null);
  const [exportingId, setExportingId] = useState<string | null>(null);

  const prevKpis = compare && prevData ? prevData.kpis : null;
  const { toast } = useToast();

  const handleExportCSV = useCallback(() => {
    if (!data) return;
    const { kpis: k, activeCampaigns: camps } = data;
    const lines: string[] = [];
    lines.push('Métrica,Valor');
    lines.push(`Impressões,${k.impressions}`);
    lines.push(`Cliques,${k.clicks}`);
    lines.push(`CTR,${k.ctr}`);
    lines.push(`Pedidos estimados,${k.leads}`);
    lines.push(`ROI estimado,${k.roi ?? '--'}`);
    lines.push(`Investido,${k.totalInvested}`);
    lines.push(`Influenciado,${k.totalInfluenced}`);
    lines.push('');
    lines.push('Campanha,Categoria,Regiões,Status,Impressões,Cliques,CTR');
    for (const c of camps) {
      lines.push(`"${c.name}","${c.category}","${c.regions?.join('; ') ?? 'Todas'}",${c.active ? 'Ativa' : 'Pausada'},${c.impressions},${c.clicks},${c.ctr}`);
    }
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    downloadBlob(blob, `mustauto-dashboard-${month}.csv`);
  }, [data, month]);

  const handleExportPDF = useCallback(async () => {
    if (!data) return;
    setExporting(true);
    try {
      const res = await api.get('/partners/my/dashboard/export', {
        params: { month, format: 'pdf' },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      downloadBlob(blob, `mustauto-dashboard-${month}.pdf`);
    } catch {
      toast({
        type: 'error',
        title: 'Não foi possível gerar o PDF. Tente novamente.',
      });
    } finally {
      setExporting(false);
    }
  }, [data, month, toast]);

  const handleExportCampaign = useCallback(async (campaignId: string, campaignName: string) => {
    setExportingId(campaignId);
    try {
      const res = await api.get(`/partners/my/campaigns/${campaignId}/export`, {
        params: { format: 'pdf' },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mustauto-campanha-${campaignName.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({
        type: 'error',
        title: 'Não foi possível gerar o PDF. Tente novamente.',
      });
    } finally {
      setExportingId(null);
    }
  }, [toast]);

  // O erro ANTES do carregando: com o gate unico `isLoading || !data`, a busca
  // que falha deixa a tela inteira de esqueleto, girando para sempre, sem frase
  // e sem saida. Aqui isso apaga o painel de metricas onde o parceiro decide
  // quanto investir no mes.
  if (isError && !data) {
    return (
      <ErrorState
        title="Não foi possível carregar seu painel"
        error={error}
        onRetry={() => void refetch()}
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <SkeletonKPIGrid />
        <SkeletonChart />
        <SkeletonTable rows={4} />
      </div>
    );
  }

  const { partner, tierInfo, kpis, chartData, activeCampaigns } = data;

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">{partner.name}</h1>
          <p className="text-sm text-muted-foreground">
            Sua marca apareceu no momento de decisão de compra real, não em clique aleatório
          </p>
          {tierInfo && (
            <TierProgress tierInfo={tierInfo} onUpgradeClick={() => setShowUpgradeModal(true)} />
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={compare}
              onChange={(e) => setCompare(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-primary"
            />
            Comparar com anterior
          </label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-md border border-border bg-card px-3 py-1.5 text-sm"
          />
          <ExportDropdown
            onExportCSV={handleExportCSV}
            onExportPDF={handleExportPDF}
            loading={exporting}
          />
        </div>
      </div>

      {/* Balance Widget */}
      <BalanceWidget />

      {/* Próximos passos (onboarding checklist) */}
      <PartnerNextStepsCard partnerId={partner.id} hasCampaign={activeCampaigns.length > 0} />

      {/* ROI Hero */}
      {kpis.roi !== null && (
        <ROIHero
          roi={kpis.roi}
          invested={kpis.totalInvested}
          influenced={kpis.totalInfluenced}
          budgetUsed={kpis.budgetUsed}
          budgetTotal={kpis.budgetTotal}
        />
      )}

      {/* KPIs Row 1 */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KPICard
          title="Impressões"
          value={formatNumber(kpis.impressions)}
          icon={<Eye size={20} />}
          subtitle={kpis.impressionsGuarantee > 0
            ? `Garantia: ${formatNumber(kpis.impressionsGuarantee)}`
            : undefined}
          {...(prevKpis ? calcDelta(kpis.impressions, prevKpis.impressions) : {})}
        />
        <KPICard
          title="Cliques"
          value={formatNumber(kpis.clicks)}
          icon={<MousePointerClick size={20} />}
          {...(prevKpis ? calcDelta(kpis.clicks, prevKpis.clicks) : {})}
        />
        <KPICard
          title="CTR"
          value={formatPercent(kpis.ctr)}
          icon={<Percent size={20} />}
          {...(prevKpis ? calcDelta(kpis.ctr, prevKpis.ctr) : {})}
        />
      </div>

      {/* KPIs Row 2 */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KPICard
          title="Pedidos estimados"
          value={formatNumber(kpis.leads)}
          icon={<ShoppingCart size={20} />}
          subtitle="estimado"
          {...(prevKpis ? calcDelta(kpis.leads, prevKpis.leads) : {})}
        />
        <KPICard
          title="ROI estimado"
          value={kpis.roi !== null ? `${kpis.roi}x` : '--'}
          icon={<TrendingUp size={20} />}
          subtitle={kpis.roi !== null ? 'receita estimada / investido' : 'Sem dados suficientes'}
          {...(prevKpis && kpis.roi !== null && prevKpis.roi !== null ? calcDelta(kpis.roi, prevKpis.roi) : {})}
        />
        <KPICard
          title="Orçamentos influenciados"
          value={formatNumber(kpis.leads)}
          icon={<DollarSign size={20} />}
          subtitle="estimado"
          {...(prevKpis ? calcDelta(kpis.leads, prevKpis.leads) : {})}
        />
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Impressões por dia</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <EmptyState
              icon={<BarChart3 size={28} />}
              title="Sem dados no período"
              description="Não há impressões registradas para o período selecionado. Tente selecionar outro mês."
            />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v: string) => {
                    const d = new Date(v);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                  }}
                  fontSize={12}
                />
                <YAxis fontSize={12} />
                <Tooltip
                  formatter={(value) => [formatNumber(Number(value)), 'Impressões']}
                  labelFormatter={(label) => {
                    const d = new Date(String(label));
                    return d.toLocaleDateString('pt-BR');
                  }}
                />
                <Bar dataKey="impressions" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Campaigns Table */}
      <Card>
        <CardHeader>
          <CardTitle>Campanhas ativas</CardTitle>
        </CardHeader>
        <CardContent>
          {activeCampaigns.length === 0 ? (
            <EmptyState
              icon={<Megaphone size={28} />}
              title="Nenhuma campanha ativa"
              description="Você ainda não tem campanhas ativas neste período. Crie uma nova campanha para alcançar oficinas parceiras."
              action={{ label: 'Criar campanha', href: '/campaigns/new' }}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campanha</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Regiões</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Impressões</TableHead>
                  <TableHead className="text-right">Cliques</TableHead>
                  <TableHead className="text-right">CTR</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeCampaigns.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.category}</TableCell>
                    <TableCell>
                      {c.regions && c.regions.length > 0
                        ? c.regions.join(', ')
                        : 'Todas'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={c.active ? 'success' : 'secondary'}>
                        {c.active ? 'Ativa' : 'Pausada'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(c.impressions)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(c.clicks)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatPercent(c.ctr)}
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={() => handleExportCampaign(c.id, c.name)}
                        disabled={exportingId === c.id}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-muted disabled:opacity-60"
                        title="Exportar PDF"
                      >
                        {exportingId === c.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <FileText size={14} />
                        )}
                        PDF
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {showUpgradeModal && (
        <TierUpgradeModal
          currentTier={partner.tier}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
    </motion.div>
  );
}
